import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import type { BlockedWordsMutationResponse, BlockedWordsResponse } from './types';

/* ------------------------------------------------------------------ */
/*  Query keys                                                        */
/* ------------------------------------------------------------------ */

export const blockedWordsKeys = {
  all: ['blocked-words'] as const,
  list: ['blocked-words', 'list'] as const,
  check: ['blocked-words', 'check'] as const,
};

export function useBlockedWords() {
  return useQuery({
    queryKey: blockedWordsKeys.list,
    queryFn: () => api.get<BlockedWordsResponse>('/api/obd/blocked-words'),
  });
}

/* ------------------------------------------------------------------ */
/*  Mutations                                                         */
/* ------------------------------------------------------------------ */

export function useAddBlockedWords() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (words: string[]) =>
      api.post<BlockedWordsMutationResponse>('/api/obd/blocked-words', { words }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: blockedWordsKeys.all });
    },
  });
}

export function useRemoveBlockedWords() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (words: string[]) =>
      api.delete<BlockedWordsMutationResponse>('/api/obd/blocked-words', {
        data: { words },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: blockedWordsKeys.all });
    },
  });
}

/* ------------------------------------------------------------------ */
/*  Validation hook (any role — cached 5 min)                         */
/* ------------------------------------------------------------------ */

export function useBlockedWordsCheck() {
  return useQuery({
    queryKey: blockedWordsKeys.check,
    queryFn: () => api.get<BlockedWordsResponse>('/api/obd/blocked-words/check'),
    staleTime: 5 * 60 * 1000,
  });
}

/* ------------------------------------------------------------------ */
/*  Shared matching utility                                           */
/* ------------------------------------------------------------------ */

/**
 * Tokenise `text` into individual words and return every word that
 * matches the blocked list. Case-insensitive, whole-word match,
 * punctuation stripped.
 *
 * @returns array of matched blocked words (empty = clean)
 */
export function findBlockedWords(
  text: string,
  blockedWords: string[],
): string[] {
  if (!text || blockedWords.length === 0) return [];

  const normalize = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/gi, " ")
      .trim()
      .replace(/\s+/g, " ");
  const normalizedText = normalize(text);

  const matched = new Set<string>();
  for (const blockedWord of blockedWords) {
    const normalizedBlocked = normalize(blockedWord);
    if (!normalizedBlocked) continue;
    const regex = new RegExp(
      `(^|\\s)${normalizedBlocked.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?=\\s|$)`,
      "i"
    );
    if (regex.test(normalizedText)) {
      matched.add(blockedWord);
    }
  }
  return [...matched];
}

// Add this to features/blocked-words/api.ts

export function useUploadBlockedWordsFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post<{ added: number; total: number }>(
        '/api/obd/blocked-words/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: blockedWordsKeys.all });
    },
  });
}